import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { faAngleLeft, faAngleRight, faCircle, faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AllProductsPagination, AscendingOrder, DecendingOrder, GetAndCreateProductLists } from "../../../../services/ProductService/AllProductList/AllProductServices";
import TableActionPopUp from "./TableActionPopUp";

export default function FiltersTable({ onSelectColumns, onSelectedIds, onSuccess, onColumnSearch, Data }) {

    const [allProductList, setAllProductList] = useState(null);
    const [filteredProductList, setFilteredProductList] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState([]);
    const [showPopup, setShowPopup] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
    const [searchTerms, setSearchTerms] = useState({});
    const navigate = useNavigate();
    const popupRef = useRef(null);
    const [loading, setLoading] = useState(false);
    // const [pageCount, setPageCount] = useState(1);
    // const [nextPageLink, setNextPageLink] = useState(null);
    // const [previousPageLink, setPreviousPageLink] = useState(null);

    const columnKeyMap = {
        'Product Id': 'p_id',
        'Status': 'active',
        'Product Name': 'name',
        'Product Category': 'category_p',
        'Product HSN Code': 'p_hsn_code_no',
        // 'Assigned To': 'assigned_to',
        'Verified': 'is_verified',
        'Product Added By': 'p_add_by',
        'Product Added Date': 'p_add_date',
        'Product Added Time': 'p_add_time',
        'Product Last Modified By': 'p_last_modified_by',
        'Product Last Modified Date': 'p_last_add_date',
        'Product Last Modified Time': 'p_last_add_time',
        'Product Owner': 'p_owner',
        'Product Recommended for': 'p_recommended_for',
        'Tags': 'tag',
        'Next Step': 'next_step',
        'Default Unit': 'default_unit',
        'Indicative Min Price': 'indicative_min_price',
        'Indicative Max Price': 'indicative_max_price',
        'Product Price': 'p_price',
        'Product Old Price': 'p_old_price',
        'Product Discount': 'p_discount',
        // 'Product Images': 'product_images',
        'Production Capacity': 'production_capacity',
        'Production Capacity Unit': 'production_capacity_unit',
        'Present Stock Status': 'present_stock_status',
        'Production Lead Time': 'production_lead_time',
        'Minimum Order Quantity': 'min_order_quantity',
        'Minimum Order Quantity Unit': 'min_order_quantity_unit',
        // 'Attached Product Certificates': 'attached_product_certificates',
        'Product Life': 'p_life',
        'Product LifeSpan': 'p_lifespan',
        'Product Certifications': 'p_certification',
        'Product Description': 'p_description',
        'Product Varients': 'p_varient'
    };
    const defaultVisibleColumns = [
        'Product Id',
        'Status',
        'Product Name',
        'Product Category',
        'Product HSN Code',
        'Assigned To',
        'Tags'
    ];

    // const getAllProductList = async () => {
    //     setLoading(true);
    //     try {
    //         const response = await GetAndCreateProductLists('GET', {});
    //         setAllProductList(response.data.results);
    //         setFilteredProductList(response.data.results);
    //         setNextPageLink(response.data.links.next)
    //         setPreviousPageLink(response.data.links.previous)
    //     } catch (error) {
    //         console.error("Error fetching product list:", error);
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const handleClickOutside = (event) => {
        if (popupRef.current && !popupRef.current.contains(event.target)) {
            setShowPopup(false);
        }
    };

    const handleIconClick = (id, event) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect(); // Get icon's position

        setPopupPosition({
            x: rect.left - 100, // Adjust the left position to be on the left side of the icon
            y: rect.top + 10 + window.scrollY // Adjust for any scroll position
        });

        setSelectedId(id);
        setShowPopup(true);
    };

    const handleProductClick = (id) => {
        if (id) {
            navigate(`/admin-dashboard/product-module/view-product/${id}`);
        }
    }

    const handleSelectAll = () => {
        if (selectedIds.length === filteredProductList.length) {
            // If all filtered items are selected, deselect all
            setSelectedIds([]);
        } else {
            // Select all visible (filtered) products
            setSelectedIds(filteredProductList.map(item => item.p_id));
        }
    };

    // ✅ Pass selected Ids to parent and close modal
    if (selectedIds.length >= 0) {
        if (typeof onSelectedIds === "function") {
            onSelectedIds(selectedIds); // Pass data up
        }
    };

    const handleSelectItem = (id) => {
        setSelectedIds((prevSelectedIds) =>
            prevSelectedIds.includes(id)
                ? prevSelectedIds.filter((selectedId) => selectedId !== id) // Deselect
                : [...prevSelectedIds, id] // Select
        );
    };

    const handleSearch = (col, value) => {
        const updatedSearchTerms = { ...searchTerms, [col]: value };
        setSearchTerms(updatedSearchTerms);

        // Filter logic
        const filtered = allProductList.filter(item =>
            visibleColumns.every(col => {
                const searchTerm = updatedSearchTerms[col]?.toString().toLowerCase();
                const value = item[columnKeyMap[col]]?.toString().toLowerCase();
                return !searchTerm || (value && value.includes(searchTerm));
            })
        );
        setFilteredProductList(filtered);
    };

    const onSearchColumn = (columnName) => {
        console.log("Filtering by column:", columnName);

        if (columnName.length === 1) {
            setFilteredProductList(allProductList); // Reset to full list
            setVisibleColumns(defaultVisibleColumns); // Show default columns
            return;
        }

        // Normalize input to match `columnKeyMap` keys
        const formattedColumnName = Object.keys(columnKeyMap).find(
            key => key.toLowerCase() === columnName.toLowerCase()
        );

        if (!formattedColumnName) {
            console.warn(`Column "${columnName}" not found in columnKeyMap`);
            return;
        }

        const columnKey = columnKeyMap[formattedColumnName];

        // Filter the product list to display only the selected column
        const filtered = allProductList.map(item => ({
            p_id: item.p_id, // Always keep Product ID
            [columnKey]: item[columnKey] || "-" // Show only the selected column
        }));

        setFilteredProductList(filtered);
        setVisibleColumns(["Product Id", formattedColumnName]); // Ensure Product ID is always visible
    };

    const [columnSortOrder, setColumnSortOrder] = useState({}); // Track sorting order per column

    const handleOrder = async (col) => {
        setLoading(true);
        const newOrder = columnSortOrder[col] === "desc" ? "asc" : "desc";
        setColumnSortOrder((prev) => ({ ...prev, [col]: newOrder }));
        const fieldName = columnKeyMap[col];
        const params = { ordering: fieldName, order_type: newOrder };

        try {
            let response;
            if (newOrder === "desc") {
                response = await DecendingOrder("GET", { params });
            } else if (newOrder === 'asc') {
                response = await AscendingOrder("GET", { params });
            }
            console.log(response.data);
            // setFilteredProductList(response.data.results);
        } catch (error) {
            console.error("Error sorting data:", error);
        } finally {
            setLoading(false);
        }
    };

    // const handleChangePage = async (condition) => {
    //     if (condition === 'previous') {
    //         if (previousPageLink) {
    //             const params = { page: pageCount - 1 }
    //             try {
    //                 const response = await AllProductsPagination("GET", { params });
    //                 setAllProductList(response.data.results);
    //                 setFilteredProductList(response.data.results);
    //                 setNextPageLink(response.data.links.next)
    //                 setPreviousPageLink(response.data.links.previous)
    //             } catch (error) {
    //                 console.error(error);
    //             }
    //             setPageCount(pageCount - 1)
    //         }
    //     } else if (condition === 'next') {
    //         if (nextPageLink) {
    //             const params = { page: pageCount + 1 }
    //             try {
    //                 const response = await AllProductsPagination("GET", { params });
    //                 setAllProductList(response.data.results);
    //                 setFilteredProductList(response.data.results);
    //                 setNextPageLink(response.data.links.next)
    //                 setPreviousPageLink(response.data.links.previous)
    //             } catch (error) {
    //                 console.error(error);
    //             }
    //             setPageCount(pageCount + 1)
    //         }
    //     }
    // }

    useEffect(() => {
        
        if (Data) {
            const flatData = Data.flat(); // Flatten nested arrays
            const uniqueData = Array.from(new Set(flatData.map(item => JSON.stringify(item)))).map(item => JSON.parse(item));
        
            setAllProductList(uniqueData);
            setFilteredProductList(uniqueData);
        }        
        // getAllProductList();
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // useEffect(() => {
    //     // getAllProductList();

    //     // setPageCount(1)
    //     // setNextPageLink(null)
    //     // setPreviousPageLink(null)
    // }, [onRefresh]);


    useEffect(() => {
        if (onSelectColumns && onSelectColumns.length > 0) {
            setVisibleColumns(onSelectColumns);
        } else {
            setVisibleColumns(defaultVisibleColumns);
        }
    }, [onSelectColumns]);

    useEffect(() => {
        if (onSuccess) {
            // getAllProductList()
            setSelectedIds([])
        }
        // setPageCount(1)
        // setNextPageLink(null)
        // setPreviousPageLink(null)
    }, [onSuccess])

    useEffect(() => {
        if (onColumnSearch && typeof onColumnSearch === "string") {
            onSearchColumn(onColumnSearch);
        }   
    }, [onColumnSearch]);

    return (
        <>
            <div className="overflow-scroll">
                <table className="min-w-full table-auto border-collapse">
                    <thead className="border border-gray-300 bg-gray-100">
                        <tr className="">
                            <th>
                                <input
                                    type="checkbox"
                                    className="scale-125 border border-gray-300"
                                    checked={filteredProductList.length > 0 && selectedIds.length === filteredProductList.length}
                                    onChange={handleSelectAll}
                                />
                            </th>
                            {visibleColumns.map((col) => (
                                <th key={col} className="w-fit text-center border border-gray-300 px-4 py-2 text-gray-600 whitespace-nowrap ">
                                    {col} &nbsp; <span
                                        key={col}
                                        className="px-2 py-0.5 font-bold border bg-white rounded-lg hover:bg-gray-300 cursor-pointer"
                                        onClick={() => handleOrder(col)}>{columnSortOrder[col] === 'desc' ? "\u2191" : "\u2193"}</span>
                                </th>
                            ))}
                            <th className="w-fit text-center border border-gray-300 px-4 py-2 text-gray-600">Actions</th>
                        </tr>
                        <tr className="bg-[#F7F7F7]">
                            <td className="border border-gray-300 px-4 py-2"></td>
                            {visibleColumns.map((col) => (
                                <td key={`search-${col}`} className="px-4 py-2 border border-gray-300 w-full">
                                    <input
                                        type="text"
                                        className="border border-gray-300 rounded-2xl px-3 outline-none"
                                        placeholder={`Search ${col}...`}
                                        onChange={(e) => handleSearch(col, e.target.value)}
                                    />
                                </td>
                            ))}
                            <td className="px-4 py-2 border border-gray-300"></td>
                        </tr>
                    </thead>
                    <tbody className="text-[14px] text-[#333]">
                        {loading ? (
                            <tr>
                                <td colSpan={visibleColumns.length + 2} className="text-center py-4 text-xl font-bold text-gray-500">
                                    Fetching Data...
                                </td>
                            </tr>
                        ) : (
                            filteredProductList.map((item) => (
                                <tr key={item.p_id} className="hover:bg-gray-100">
                                    <td className="border border-gray-300 px-4 py-2 text-center">
                                        <input
                                            type="checkbox"
                                            className="scale-125 border border-gray-300 cursor-pointer"
                                            checked={selectedIds.includes(item.p_id)}
                                            onChange={() => handleSelectItem(item.p_id)}
                                        />
                                    </td>
                                    {visibleColumns.map((col) => (
                                        <td key={col} className="border border-gray-300 px-4 py-2 cursor-pointer text-center"
                                            onClick={() => handleProductClick(item.p_id)}>
                                            {col === "Verified"
                                                ? item[columnKeyMap[col]] === true
                                                    ? <div className="flex justify-center"><p className="text-green-600 px-3 py-1 bg-green-200 w-fit rounded-lg">Verified</p></div>
                                                    : <div className="flex justify-center"><p className="text-red-600 px-3 py-1 bg-red-200 w-fit rounded-lg">Not Verified</p></div>
                                                : col === "Status"
                                                    ? item[columnKeyMap[col]] === true
                                                        ? <div className="flex justify-center"><div className="text-green-600 px-3 py-1 bg-green-200 w-fit rounded-lg flex items-center gap-1"><p className="text-xs"><FontAwesomeIcon icon={faCircle} /></p> Active</div></div>
                                                        : <div className="flex justify-center"><div className="text-red-600 px-3 py-1 bg-red-200 w-fit rounded-lg flex items-center gap-1"><p className="text-xs"><FontAwesomeIcon icon={faCircle} /></p> Deactive</div></div>
                                                    : (item[columnKeyMap[col]] || "-")}
                                        </td>
                                    ))}
                                    <td className="border border-gray-300 px-4 py-2 text-center">
                                        <span
                                            className="cursor-pointer"
                                            onClick={(event) => handleIconClick(item.p_id, event)}
                                        >
                                            <FontAwesomeIcon icon={faEllipsisVertical} />
                                        </span>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {/* Pagination */}
            {/* <div className="flex items-center justify-center mt-5">
                <div className="flex items-center gap-6">
                    <button className={`text-white px-4 py-1 rounded-lg ${previousPageLink ? "bg-sky-500 hover:bg-sky-300" : "bg-gray-500 opacity-50 cursor-not-allowed"}`}
                        disabled={!previousPageLink}
                        onClick={() => handleChangePage('previous')}>
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </button>
                    <p className="font-bold">{pageCount}</p>
                    <button className={`text-white px-4 py-1 rounded-lg ${nextPageLink ? "bg-sky-500 hover:bg-sky-300" : "bg-gray-500 opacity-50 cursor-not-allowed"}`}
                        disabled={!nextPageLink}
                        onClick={() => handleChangePage('next')}>
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
            </div> */}
            {showPopup && (
                <div ref={popupRef} style={{ position: "absolute", top: `${popupPosition.y}px`, left: `${popupPosition.x}px`, zIndex: 10 }}>
                    <TableActionPopUp id={selectedId} onSuccess={() => setShowPopup(false)} />
                </div>)}
        </>
    )
}
